import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type NotificationType =
  | 'warning'
  | 'error'
  | 'success'
  | 'info'
  | 'pending';

export type NotificationCategory =
  | 'check-in'
  | 'check-out'
  | 'scale'
  | 'schedule'
  | 'system'
  | 'user'
  | 'professional'
  | 'establishment'
  | 'company';

export interface Notification {
  id: string;
  backendId?: number;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  subtitle?: string;
  description?: string;
  code?: string;
  department?: string;
  professionalName?: string;
  date?: string;
  timeRange?: string;
  isRead: boolean;
  createdAt: Date;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
}

export type CreateNotificationData = Omit<
  Notification,
  'id' | 'isRead' | 'createdAt'
>;

interface NotificationState {
  notifications: Notification[];
  isDrawerOpen: boolean;
  unreadCount: number;

  addNotification: (data: CreateNotificationData) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAsUnread: (id: string) => void;
  markAllAsRead: () => void;
  clearAllNotifications: () => void;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
}

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      isDrawerOpen: false,
      unreadCount: 0,

      addNotification: (data: CreateNotificationData) => {
        const currentNotifications = get().notifications;

        if (
          data.backendId &&
          currentNotifications.some((n) => n.backendId === data.backendId)
        ) {
          return;
        }

        const newNotification: Notification = {
          ...data,
          id: generateId(),
          isRead: false,
          createdAt: new Date(),
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications],
          unreadCount: state.unreadCount + 1,
        }));
      },

      removeNotification: (id: string) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          const wasUnread = notification && !notification.isRead;
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread ? state.unreadCount - 1 : state.unreadCount,
          };
        });
      },

      markAsRead: (id: string) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.isRead) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, isRead: true } : n,
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAsUnread: (id: string) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || !notification.isRead) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, isRead: false } : n,
            ),
            unreadCount: state.unreadCount + 1,
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            isRead: true,
          })),
          unreadCount: 0,
        }));
      },

      clearAllNotifications: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      setDrawerOpen: (open: boolean) => {
        set({ isDrawerOpen: open });
      },

      toggleDrawer: () => {
        set((state) => ({ isDrawerOpen: !state.isDrawerOpen }));
      },
    }),
    {
      name: 'notification-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    },
  ),
);

export const useUnreadNotifications = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  return notifications.filter((n) => !n.isRead);
};

export const useNotificationsByCategory = (category: NotificationCategory) => {
  const notifications = useNotificationStore((state) => state.notifications);
  return notifications.filter((n) => n.category === category);
};
